import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   L'environment map — l'angle mort que les trois architectures évaluées
   avaient toutes oublié (PLAN.md §4.6).

   Sans elle, le rendu est lisible et PLAT. Les vaisseaux et les boss
   apparaissent en aplats non modelés, et la 3D ne se lit plus que sur le décor
   de fond. C'est exactement l'écart entre « fonctionnel » et « beau », et il ne
   coûte que ces quelques lignes.

   Sur du low-poly métallique stylisé, une PMREM de 64 px est le meilleur
   rapport joli/coût qui existe : réponse spéculaire correcte, coût runtime
   NUL — elle est calculée une fois au boot — et aucune recompilation en jeu.

   ⚠️ Elle doit être posée AVANT le warm-up des shaders. L'envMap est un
   `#define` : la poser après invaliderait toutes les variantes déjà compilées,
   et le freeze qu'on cherchait à supprimer réapparaîtrait à la première vague.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Dégradé néon procédural : cyan au zénith, magenta à l'horizon, noir au nadir.
 *  Aucun asset à charger, aucun octet à télécharger. */
function gradientTexture(size = 64): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  const top = [0x0a, 0x2a, 0x3a];
  const mid = [0x1a, 0x0e, 0x2e];
  const bot = [0x02, 0x02, 0x08];

  for (let y = 0; y < size; y++) {
    const v = y / (size - 1);
    // Deux segments : zénith → horizon, puis horizon → nadir.
    const from = v < 0.5 ? top : mid;
    const to = v < 0.5 ? mid : bot;
    const t = v < 0.5 ? v * 2 : (v - 0.5) * 2;
    for (let x = 0; x < size; x++) {
      const o = (y * size + x) * 4;
      data[o] = from[0]! + (to[0]! - from[0]!) * t;
      data[o + 1] = from[1]! + (to[1]! - from[1]!) * t;
      data[o + 2] = from[2]! + (to[2]! - from[2]!) * t;
      data[o + 3] = 255;
    }
  }

  const tex = new THREE.DataTexture(data, size, size);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Construit l'environment map et la pose sur la scène.
 * Coût : ~2 ms au boot, zéro par frame. À appeler AVANT `Warmup.run()`.
 */
export function buildEnvironment(renderer: THREE.WebGLRenderer, scene: THREE.Scene): THREE.Texture {
  const src = new THREE.Scene();
  const tex = gradientTexture(64);
  src.background = tex;

  const pmrem = new THREE.PMREMGenerator(renderer);
  const env = pmrem.fromScene(src, 0.04).texture;
  scene.environment = env;

  // Le générateur et la source ne servent plus : les garder retiendrait des
  // render targets pour rien.
  pmrem.dispose();
  tex.dispose();

  return env;
}

/* ── Rim light par onBeforeCompile ────────────────────────────────────────
   Un liseré de lumière sur la silhouette, injecté dans le matériau standard
   plutôt que rendu par une passe dédiée.

   ⚠️ Depuis le J13, les coques n'appellent PLUS `applyRimLight` : le liseré est
   intégré au matériau unique de `materials/hullMaterial.ts`, avec le masque
   d'émission de l'atlas, dans un seul `onBeforeCompile`. Deux injections sur le
   même matériau se seraient écrasées l'une l'autre — `onBeforeCompile` est un
   champ, pas une liste — et la seconde aurait silencieusement annulé la
   première.

   La fonction reste ici pour tout matériau HORS coque qui voudrait le même
   liseré. Elle n'a aujourd'hui aucun appelant.

   C'est un avantage concret et souvent sous-estimé du chemin WebGL2 : la
   possibilité de greffer trois lignes de GLSL sur un matériau existant sans
   rien réécrire. Le chemin WebGPU aurait imposé de repasser tout le matériau
   en TSL — d'où, entre autres, le choix du plan de rester sur WebGL2 (§1.4). */

export const RIM_COLOR = new THREE.Color(0x7fa8c8);
export const RIM_STRENGTH = 0.35;

export function applyRimLight(material: THREE.Material): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms['uRimColor'] = { value: RIM_COLOR };
    shader.uniforms['uRimStrength'] = { value: RIM_STRENGTH };

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uRimColor;
uniform float uRimStrength;`,
      )
      .replace(
        '#include <opaque_fragment>',
        `float rimFresnel = pow(1.0 - abs(dot(normalize(vViewPosition), normal)), 2.5);
outgoingLight += uRimColor * rimFresnel * uRimStrength;
#include <opaque_fragment>`,
      );
  };
  // ⚠️ Obligatoire : sans changer la clé de cache, three réutiliserait le
  // programme déjà compilé pour ce type de matériau et l'injection serait
  // silencieusement ignorée. Le symptôme — « mon shader custom ne fait rien » —
  // est l'un des plus déroutants de three.js.
  material.customProgramCacheKey = () => 'rim-v1';
}
